<template>
  <div class="work-editor-page">
    <div class="we-page" v-loading="loading">
      <div class="we-toolbar">
        <el-button size="small" icon="el-icon-delete" @click="onClear">清空</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" :loading="saving" @click="onSave">
          保存
        </el-button>
      </div>

      <type-panel :active-type="activeType" @select="onSelectType" />
      <main class="we-center">
        <explain-editor v-model="explain" />

        <draggable v-model="questions" handle=".we-drag-handle" :animation="200" ghost-class="we-drag-ghost"
          @end="onDragEnd">
          <question-card v-for="(q, idx) in questions" :key="q.id" :question="q" :index="idx + 1"
            :is-last="idx === questions.length - 1" :active="activeId === q.id" :powerlist="powerlist"
            @select="selectQuestion" @remove="removeQuestion" @move="moveQuestion" />
        </draggable>

        <div v-if="!questions.length" class="we-add-empty" @click="$message.warning('请在左侧选择题型')">
          <i class="el-icon-plus"></i>
          点击左侧题型添加题目
        </div>
      </main>

      <setting-panel v-model="settings" :themebind="themebind" :themetype="themetype" :courselist="courselist"
        :time-readonly="timeReadonly" />
    </div>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'
  import TypePanel from './components/TypePanel.vue'
  import ExplainEditor from './components/ExplainEditor.vue'
  import QuestionCard from './components/QuestionCard.vue'
  import SettingPanel from './components/SettingPanel.vue'
  import { TYPE_META, createEmptyQuestion, createDefaultSettings } from './utils/questionTypes'
  import {
    fromDrawingList,
    toDrawingList,
    fromWorkConfig,
    toWorkConfig,
    formatSupplementTime,
    compareTime
  } from './utils/convert'

  export default {
    name: 'WorkEditorMain',
    components: {
      draggable,
      TypePanel,
      ExplainEditor,
      QuestionCard,
      SettingPanel
    },
    props: {
      // create | edit，由页面组件传入，避免靠路由猜测
      mode: {
        type: String,
        required: true,
        validator: (v) => ['create', 'edit'].includes(v)
      }
    },
    data() {
      return {
        loading: false,
        saving: false,
        activeType: 'desc',
        activeId: null,
        explain: '',
        questions: [],
        settings: createDefaultSettings(),
        powerlist: [],
        courselist: [],
        timeReadonly: false
      }
    },
    computed: {
      isCreate() {
        return this.mode === 'create'
      },
      themebind() {
        return this.$route.query.themebind
      },
      themetype() {
        return this.$route.query.themetype
      },
      themeId() {
        return this.$route.query.themeId
      },
      workId() {
        return this.$route.query.workId
      }
    },
    mounted() {
      this.init()
    },
    methods: {
      async init() {
        this.loading = true
        try {
          await Promise.all([this.loadPowerlist(), this.loadCourse()])
          if (this.isCreate) {
            this.explain = ''
            this.questions = []
            this.settings = createDefaultSettings()
          } else {
            if (!this.workId || this.workId === '-1') {
              this.$message.error('缺少作业 ID')
              return
            }
            await this.loadForm(this.workId)
          }
        } finally {
          this.loading = false
        }
      },
      loadPowerlist() {
        return this.$Api.Form.getAllPower()
          .then((res) => {
            this.powerlist = res.data || []
          })
          .catch(() => {
            this.powerlist = []
          })
      },
      loadCourse() {
        if (String(this.themebind) !== '1') return Promise.resolve()
        const id = this.$route.query.courseId
        if (!id) return Promise.resolve()
        return this.$Api.Course.getLessonList(id)
          .then((res) => {
            this.courselist = res.data || []
          })
          .catch(() => {
            this.courselist = []
          })
      },
      loadForm(id) {
        return this.$Api.Form.getTeaForm(id)
          .then((res) => {
            const data = res.data || {}
            let content = []
            let answerMap = {}
            try {
              content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content || []
            } catch (e) {
              content = []
            }
            try {
              answerMap = typeof data.answer === 'string' ? JSON.parse(data.answer) : (data.answer || {})
            } catch (e) {
              answerMap = {}
            }
            const parsed = fromDrawingList(content)
            this.explain = parsed.explain || ''
            const questions = (parsed.questions || []).map((q) => {
              const fallbackAnswer = answerMap[q.vModel]
              if (fallbackAnswer === undefined || fallbackAnswer === null || fallbackAnswer === '') return q
              if (q.type === 'multi') {
                if (!Array.isArray(q.answer) || !q.answer.length) {
                  q.answer = Array.isArray(fallbackAnswer) ? fallbackAnswer : [fallbackAnswer]
                }
                return q
              }
              // 问答题参考答案、判断/单选正确答案：content.defaultValue 常被清空，需从 answer 回填
              if ((q.type === 'qa' || q.type === 'tf' || q.type === 'single') && !q.answer) {
                q.answer = Array.isArray(fallbackAnswer)
                  ? fallbackAnswer.filter((v) => v != null && v !== '').join(',')
                  : String(fallbackAnswer)
              }
              return q
            })
            this.questions = questions
            this.settings = fromWorkConfig(data.title)
            // 作业已结束则时间只读
            if (this.settings.endTime) {
              const endArr = this.settings.endTime.split('')
              endArr.splice(10, 1, ' ')
              if (new Date(endArr.join('')).getTime() < Date.now()) {
                this.timeReadonly = true
              }
            }
            if (this.questions.length) {
              this.activeId = this.questions[0].id
              this.activeType = this.questions[0].type
            }
          })
          .catch((err) => {
            this.$message.error(err || '加载作业失败')
          })
      },
      onSelectType(type) {
        this.activeType = type
        if (type === 'desc') {
          this.$message.success('可在上方编辑作业说明')
          return
        }
        const q = createEmptyQuestion(type)
        this.questions.push(q)
        this.activeId = q.id
        this.$nextTick(() => {
          const el = document.querySelector('.we-question-card.active')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
        this.$message.success(`已添加${TYPE_META[type].name}`)
      },
      selectQuestion(id) {
        this.activeId = id
        const q = this.questions.find((item) => item.id === id)
        if (q) this.activeType = q.type
      },
      removeQuestion(id) {
        this.$confirm('确定删除此题？', '提示', { type: 'warning' })
          .then(() => {
            this.questions = this.questions.filter((q) => q.id !== id)
            if (this.activeId === id) {
              this.activeId = this.questions.length ? this.questions[this.questions.length - 1].id : null
            }
            this.$message.success('题目已删除')
          })
          .catch(() => { })
      },
      moveQuestion(id, delta) {
        const index = this.questions.findIndex((q) => q.id === id)
        const target = index + delta
        if (index < 0 || target < 0 || target >= this.questions.length) return
        const list = [...this.questions]
        const [item] = list.splice(index, 1)
        list.splice(target, 0, item)
        this.questions = list
        this.activeId = id
      },
      onDragEnd() {
        this.$message.success('顺序已调整')
      },
      validate() {
        if (!this.settings.workname || !String(this.settings.workname).trim()) {
          this.$message.warning('请填写作业名称')
          return false
        }
        if (!this.settings.beginTime) {
          this.$message.warning('请选择作业开始时间')
          return false
        }
        if (!this.settings.endTime) {
          this.$message.warning('请选择作业结束时间')
          return false
        }
        if (!compareTime(this.settings.beginTime, this.settings.endTime)) {
          this.$message.warning('作业结束时间应大于开始时间')
          return false
        }
        if (String(this.themetype) !== '4' && !this.questions.length) {
          this.$message.warning('作业内容不能为空，请至少添加一道题目')
          return false
        }
        for (let i = 0; i < this.questions.length; i++) {
          const q = this.questions[i]
          if (!q.title || !String(q.title).trim()) {
            this.$message.warning(`第 ${i + 1} 题题目不能为空`)
            return false
          }
          if (q.type === 'tf' && !q.answer) {
            this.$message.warning(`第 ${i + 1} 题请选择正确答案`)
            return false
          }
          if (q.type === 'single' && !q.answer) {
            this.$message.warning(`第 ${i + 1} 题请选择正确答案`)
            return false
          }
          if (q.type === 'multi' && (!Array.isArray(q.answer) || !q.answer.length)) {
            this.$message.warning(`第 ${i + 1} 题请选择正确答案`)
            return false
          }
          if (q.type === 'attach' && (!q.formats || !q.formats.length)) {
            this.$message.warning(`第 ${i + 1} 题请选择提交格式限制`)
            return false
          }
        }
        if (String(this.themebind) === '1' && !this.settings.lessonId) {
          this.$message.warning('请选择课节')
          return false
        }
        if (this.settings.subagain) {
          if (!this.settings.subStart || !this.settings.subEnd) {
            this.$message.warning('请完善补交时间')
            return false
          }
          if (!compareTime(this.settings.subStart, this.settings.subEnd)) {
            this.$message.warning('补交结束时间应大于开始时间')
            return false
          }
        }
        return true
      },
      buildParams() {
        const drawingList = toDrawingList(this.explain, this.questions)
        const workconfig = toWorkConfig(this.settings)
        const params = {
          content: JSON.stringify(drawingList),
          title: JSON.stringify(workconfig),
          themeId: this.themeId,
          needStudy: this.settings.needStudy ? 1 : 0,
          supplement: this.settings.subagain ? 1 : 0
        }
        if (this.settings.subagain) {
          params.supplementStart = formatSupplementTime(this.settings.subStart)
          params.supplementEnd = formatSupplementTime(this.settings.subEnd)
        }
        if (this.isCreate) {
          params.createrId = this.$store.state.user.userInfo.id
        } else {
          params.createrId = this.$route.query.createId
          params.id = this.workId
          params.mdQuestionnaireId = this.$route.query.mdQuestionnaireId
        }
        return params
      },
      onSave() {
        if (!this.validate()) return
        this.$confirm('是否将此问卷提交到服务器？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
          .then(() => {
            this.saving = true
            const params = this.buildParams()
            const req = this.isCreate
              ? this.$Api.Form.addForm(params)
              : this.$Api.Form.addAgainForm(params)
            return req
              .then(() => {
                this.$message.success('提交成功')
                this.$router.push({
                  path: '/workManage',
                  query: {
                    themeId: this.themeId,
                    createId: this.$route.query.createId,
                    themetype: this.themetype,
                    themebind: this.themebind,
                    courseId: this.$route.query.courseId,
                    projectId: this.$route.query.projectId
                  }
                })
              })
              .catch((err) => {
                this.$message.error(err || '提交失败')
              })
              .finally(() => {
                this.saving = false
              })
          })
          .catch(() => { })
      },
      onClear() {
        this.$confirm('是否清空已编辑好的作业？清空后不可恢复。', '提示', {
          confirmButtonText: '确定清空',
          cancelButtonText: '取消',
          type: 'warning'
        })
          .then(() => {
            this.explain = ''
            this.questions = []
            this.activeId = null
            this.activeType = 'desc'
            this.settings = createDefaultSettings()
            this.$message.success('已清空')
          })
          .catch(() => { })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .work-editor-page {
    padding-top: 10px;
    min-height: 100vh;
    width: 100%;
    box-sizing: border-box;
    background: #f0f2f8;
  }

  .we-toolbar {
    // 横跨网格全部列（从第 1 列到最后一列）
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end; // 按钮靠右
    align-items: center;
    gap: 10px; // 按钮间距
    height: 32px;
  }

  .we-page {
    background: #fff;
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr) 340px;
    gap: 16px;
    padding: 16px 24px 24px;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    min-height: 100vh;
    box-sizing: border-box;
    align-content: start; // 多余高度不再分给各行
  }

  .we-center {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    width: 100%;
  }

  .we-add-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    padding: 16px;
    border: 2px dashed #e5e6eb;
    border-radius: 10px;
    color: #86909c;
    font-size: 14px;
    cursor: pointer;
    background: #fff;
    transition: all 0.2s;

    &:hover {
      border-color: #4f6ef7;
      color: #4f6ef7;
      background: #eef1fe;
    }
  }

  .we-drag-ghost {
    opacity: 0.45;
  }

  @media (max-width: 1200px) {
    .we-page {
      grid-template-columns: 220px minmax(0, 1fr) 300px;
      padding: 12px 16px 16px;
    }
  }

  @media (max-width: 960px) {
    .we-page {
      grid-template-columns: 1fr;
    }
  }
</style>
